/**
 * useStudentSettingsState — « Paramètres » : la page ouverte, les brouillons, l'enregistrement de
 * chaque page, et la sortie gardée.
 *
 * Port de `SettingsTabNavigator.js` et de ses quatre onglets, en quatre pages qui s'enregistrent
 * chacune (décision du 13/10, `DECISIONS.md`) :
 *
 * - **Enregistrer** valide la page (le schéma de l'ancien formulaire, page par page), puis envoie
 *   `authService.updateStudentAccount` avec le corps ENTIER (`toStudentAccountPayload`) : les autres
 *   pages partent avec les valeurs du compte, sauf la classe : seule la page Profil, qui la montre,
 *   l'envoie avec `affirm_class`, et elle s'affirme côté serveur quand c'est l'élève qui poste
 *   (`StudentProfile::affirmClass()`) ; les trois autres pages n'affirment rien. Au succès,
 *   le compte est remplacé par la réponse (réducteur `currentUser`) et l'accusé s'affiche
 *   (« Tes paramètres ont bien été mis à jour », les mots de l'ancien écran de félicitations, qui
 *   n'est plus ouvert). Un refus : les erreurs de champ de la page sous leurs champs, le message du
 *   serveur au-dessus du bouton.
 * - **La sortie** gardée : `useSettingsSectionShell`, la même que le tuteur.
 * - **Les liens** : « contacte le support » et « Contacter le support » (`mailto:`), « Modifier mon mot
 *   de passe » (`account-password`), la ligne d'abonnement (`my-privileges`, abonnement en cours ou
 *   en pause), « Fermer mon compte » (`account-close`), « Confidentialité » et « Mentions légales »
 *   (navigateur intégré), « Noter l'application » (mobile seulement, la fiche du store).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useReducer, useRef, type Dispatch } from "react"
import { Keyboard } from "react-native"
import { useSelector } from "react-redux"

import { authService } from "../../../../../../data/services/authService"
import { useAccountTimezoneEntry } from "../../../hooks/useAccountTimezoneEntry"
import {
    settingsFieldErrors,
    toggleNotificationChannel,
    toStudentAccountPayload,
    validateAddressSettings,
    validateGeneralSettings,
    type ApiNotificationSettings,
    type ApiStudentSettingsUser,
    type StudentAccountEdits,
    type StudentSettingsSection,
} from "../../../../adapters/student/Account/studentSettingsAdapter"
import { type StudentSettingsViewModel } from "../../../../view-models/student/Account/studentSettingsViewModel"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { settingsViewDirty } from "../../../hooks/settingsUiState"
import { useApiMutation } from "../../../hooks/useApiMutation"
import {
    LEGAL_URL,
    OTHER_PAGE_REFUSED,
    PRIVACY_URL,
    rateAppAction,
    SETTINGS_SAVED_MESSAGE,
    SETTINGS_SUBMIT_FAILED,
    useSettingsSectionShell,
} from "../../../hooks/useSettingsSectionShell"
import {
    initialStudentSettingsUiState,
    studentSettingsUiReducer,
    type StudentSettingsUiAction,
    type StudentSettingsUiState,
} from "./studentSettingsUiState"
import { openSupportEmail } from "../../../hooks/openSupportEmail"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"

type StackNavigation = { push: (route: string, params?: object) => void }

/** Les valeurs qu'une page montre : son brouillon, sinon celles du compte. */
function sectionValues(ui: StudentSettingsUiState, vm: StudentSettingsViewModel) {
    return {
        general: ui.drafts.general ?? vm.general,
        address: ui.drafts.address ?? vm.address,
        profile: ui.drafts.profile ?? vm.profile,
    }
}

export function useStudentSettingsState(
    vm: StudentSettingsViewModel,
    ui: StudentSettingsUiState,
    dispatchUi: Dispatch<StudentSettingsUiAction>
) {
    const navigation = useNavigation() as unknown as StackNavigation
    const user = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => selectAccount(state) as ApiStudentSettingsUser | undefined
    )
    const pendingSectionRef = useRef<StudentSettingsSection | null>(null)
    const values = sectionValues(ui, vm)

    const { start, requesting } = useApiMutation({
        service: authService.updateStudentAccount,
        voice: "student",
        fallback: SETTINGS_SUBMIT_FAILED,
        onSuccess: () => {
            const section = pendingSectionRef.current
            pendingSectionRef.current = null
            if (section) dispatchUi({ type: "SAVED", section, message: SETTINGS_SAVED_MESSAGE })
        },
        onError: (message, error) => {
            const section = pendingSectionRef.current
            pendingSectionRef.current = null
            if (!section) return
            const { fields, elsewhere } = settingsFieldErrors(error, section)
            dispatchUi({ type: "INVALID", errors: fields, submitError: elsewhere ? OTHER_PAGE_REFUSED : message })
        },
    })

    const submit = useCallback(
        (section: StudentSettingsSection) => {
            if (!user || requesting) return
            const errors =
                section === "general" && values.general
                    ? validateGeneralSettings(values.general)
                    : section === "address" && values.address
                      ? validateAddressSettings(values.address)
                      : {}
            if (Object.keys(errors).length > 0) {
                dispatchUi({ type: "INVALID", errors: errors as Record<string, string>, submitError: null })
                return
            }
            Keyboard.dismiss()
            dispatchUi({ type: "SUBMITTING" })
            pendingSectionRef.current = section
            const edits: StudentAccountEdits =
                section === "general"
                    ? { general: values.general ?? undefined }
                    : section === "address"
                      ? { address: values.address ?? undefined }
                      : section === "profile"
                        ? { profile: values.profile ?? undefined }
                        : { notificationSettings: ui.drafts.preferences ?? undefined }
            start({}, toStudentAccountPayload(user, edits))
        },
        [user, requesting, values, ui.drafts.preferences, start, dispatchUi]
    )

    const { back, shell } = useSettingsSectionShell({
        ui,
        dispatchUi,
        dirty: settingsViewDirty(ui, vm),
        loading: vm.loading,
        submitting: requesting,
    })

    const edit =
        <S extends "general" | "address" | "profile">(section: S) =>
        (field: string, value: unknown) => {
            const current = values[section]
            if (current == null) return
            dispatchUi({ type: "EDIT", section, field, value: { ...current, [field]: value } })
        }

    const preferencesSource: ApiNotificationSettings | null =
        ui.drafts.preferences ?? (user?.notification_settings as ApiNotificationSettings | null | undefined) ?? null

    const timezoneEntry = useAccountTimezoneEntry()

    return {
        view: ui.view,
        hub: {
            onBack: back,
            onOpenGeneral: () => dispatchUi({ type: "OPEN", view: "general" }),
            onOpenAddress: () => dispatchUi({ type: "OPEN", view: "address" }),
            onOpenProfile: () => dispatchUi({ type: "OPEN", view: "profile" }),
            onOpenPreferences: () => dispatchUi({ type: "OPEN", view: "preferences" }),
            ...timezoneEntry,
            loading: vm.loading,
        },
        general: {
            ...shell,
            values: values.general,
            errors: ui.errors,
            onChange: edit("general"),
            subscription: vm.subscription,
            onOpenSubscription: vm.canOpenSubscription ? () => navigation.push("my-privileges") : undefined,
            onContactSupport: openSupportEmail,
            onChangePassword: () => navigation.push("account-password"),
            onSubmit: () => submit("general"),
        },
        address: {
            ...shell,
            values: values.address,
            errors: ui.errors,
            onChange: edit("address"),
            countries: vm.countries,
            onSubmit: () => submit("address"),
        },
        profile: {
            ...shell,
            values: values.profile,
            errors: ui.errors,
            onChange: edit("profile"),
            catalogs: vm.catalogs,
            profileCompletion: vm.profileCompletion,
            onSubmit: () => submit("profile"),
        },
        preferences: {
            ...shell,
            notifications: vm.notifications,
            onToggleNotification: (slug: string, channel: "push" | "email", next: boolean) =>
                dispatchUi({
                    type: "SET_PREFERENCES",
                    value: toggleNotificationChannel(preferencesSource, slug, channel, next),
                }),
            onContactSupport: openSupportEmail,
            onCloseAccount: () => navigation.push("account-close"),
            onOpenPrivacy: () => openExternalUrl(PRIVACY_URL),
            onOpenLegal: () => openExternalUrl(LEGAL_URL),
            onRateApp: rateAppAction(vm.storeUrls),
            onSubmit: () => submit("preferences"),
        },
    }
}

/** L'état de l'écran, tenu par l'écran : son brouillon de notifications nourrit aussi le view-model. */
export function useStudentSettingsUi(initialTab: unknown) {
    return useReducer(studentSettingsUiReducer, initialTab, initialStudentSettingsUiState)
}
